// Generic CSV/TSV parser. Spec 21 §4.1 (financial line item import) and
// spec 15 §3.3 (WTT import) both use a "paste headers + rows, get typed
// objects + line-tracking errors" shape. Lift the parser once here and
// give each consumer a typed wrapper.
//
// Design:
//   - Split lines on \r\n or \n; trim & drop blanks
//   - Detect separator: tabs win if line 1 contains one, else comma
//     (matches spec 15 §3.3 parseWtt logic)
//   - First non-blank line is the header row; missing required columns
//     → single error against line 1
//   - Each subsequent line → ParsedRow with original line number for
//     error messages
//
// What this parser is NOT:
//   - Quoted-CSV-with-embedded-newlines aware. The expected input is
//     paste-from-spreadsheet or paste-from-PDF, where the operator strips
//     newlines from cell content. If we ever need RFC 4180 strict, swap
//     in papaparse — but keep the same wire shape so consumers don't
//     have to change.
//   - Locale-aware for numbers. "1,200" inside a single cell would split
//     into two cells if the separator is comma. Operators paste with
//     either commas-no-thousand-separator or tabs. We trade locale
//     flexibility for predictable splits.

export type ParsedRow<T> =
  | { ok: true; line: number; raw: string; row: T }
  | { ok: false; line: number; raw: string; error: string }

export interface ParseOptions<T> {
  // Column names the parser must see in the header row (case-insensitive,
  // trimmed). Missing any of these → single error against line 1.
  required: readonly string[]
  // Optional extra columns allowed but not required.
  optional?: readonly string[]
  // Per-row mapper. Receives the raw cell values keyed by lowercased
  // header name, plus the original line number for error messages.
  // Return a typed row OR throw a `ParseError` with a friendly message
  // that gets attached to the row.
  mapRow: (cells: Record<string, string>, ctx: { line: number; raw: string }) => T
}

export class ParseError extends Error {}

export interface ParseResult<T> {
  rows: ParsedRow<T>[]
  // Convenience splits.
  valid: Array<Extract<ParsedRow<T>, { ok: true }>>
  errors: Array<Extract<ParsedRow<T>, { ok: false }>>
}

export function parseCsv<T>(raw: string, opts: ParseOptions<T>): ParseResult<T> {
  const lines = raw.split(/\r?\n/)
  // Preserve original line numbers so error messages point the operator
  // at the right row even if there are blanks in the middle of the paste.
  const indexed: { line: number; text: string }[] = []
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim()
    if (trimmed) indexed.push({ line: i + 1, text: trimmed })
  }
  if (indexed.length < 2) return { rows: [], valid: [], errors: [] }

  const header = indexed[0]
  const sep = header.text.includes('\t') ? '\t' : ','
  const headers = splitRow(header.text, sep).map((h) => h.trim().toLowerCase())

  // Required-column check. Returns one error against the header line —
  // don't bother trying to parse rows if the schema's wrong.
  const missing = opts.required
    .map((r) => r.toLowerCase())
    .filter((r) => !headers.includes(r))
  if (missing.length > 0) {
    const err: ParsedRow<T> = {
      ok: false,
      line: header.line,
      raw: header.text,
      error: `Missing required column${missing.length === 1 ? '' : 's'}: ${missing.join(', ')}`
    }
    return { rows: [err], valid: [], errors: [err] }
  }

  const allowed = new Set<string>([
    ...opts.required.map((r) => r.toLowerCase()),
    ...(opts.optional ?? []).map((o) => o.toLowerCase())
  ])
  // Extra columns are tolerated (they just won't make it into the cells
  // object) — operators often paste with trailing tab-padding. If we ever
  // want to be stricter we can add an `extraColumns: 'reject' | 'ignore'`
  // option, but tolerant by default keeps the import-flow forgiving.

  const out: ParsedRow<T>[] = []
  for (let i = 1; i < indexed.length; i++) {
    const { line, text } = indexed[i]
    const cells = splitRow(text, sep)
    const obj: Record<string, string> = {}
    for (let h = 0; h < headers.length; h++) {
      const name = headers[h]
      if (!allowed.has(name)) continue
      obj[name] = (cells[h] ?? '').trim()
    }
    try {
      const row = opts.mapRow(obj, { line, raw: text })
      out.push({ ok: true, line, raw: text, row })
    } catch (err) {
      const msg =
        err instanceof ParseError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err)
      out.push({ ok: false, line, raw: text, error: msg })
    }
  }

  const valid = out.filter(
    (r): r is Extract<ParsedRow<T>, { ok: true }> => r.ok
  )
  const errors = out.filter(
    (r): r is Extract<ParsedRow<T>, { ok: false }> => !r.ok
  )
  return { rows: out, valid, errors }
}

// splitRow — simple split on the chosen separator. No quote handling
// for the reasons in the file header. Strip a trailing empty cell from
// rows that end in the separator (Excel sometimes emits these).
function splitRow(text: string, sep: string): string[] {
  const parts = text.split(sep)
  if (parts.length > 0 && parts[parts.length - 1] === '') parts.pop()
  return parts
}
